import { useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { renderLoadingUntilDefined } from "../common/LoadingUntilDefined";
import { useCrewsQuery } from "../crew/useCrewsQuery";
import PlaylistData from "../playlist/data/PlaylistData";
import { usePlaylistMutations } from "../playlist/graphql/usePlaylistMutations";
import { CrewSelector } from "../crew/CrewSelector";
import { CyCopyMovePlaylistsModal } from "./CyCopyMovePlaylistsModal";
import { useTranslation } from "react-i18next";

interface Props {
  currentCrewIdentifier: string | undefined;
  setCurrentCrewIdentifier: (i: string | undefined) => void;
  isModalOpen: boolean;
  setIsModalOpen: (value: ((prevState: boolean) => boolean) | boolean) => void;
  performIndexRefresh: () => void;
  selectedPlaylists: readonly PlaylistData[];
}

export function CopyMovePlaylistsModal(props: Props) {
  const { t } = useTranslation();
  const { crews } = useCrewsQuery();
  const playlistMutations = usePlaylistMutations();

  const [targetCrewIdentifier, setTargetCrewIdentifier] = useState<
    string | undefined
  >(props.currentCrewIdentifier);

  const toggle = () => props.setIsModalOpen((v) => !v);

  function copySelectedPlaylistsToCrew() {
    playlistMutations.copyPlaylists(
      props.selectedPlaylists,
      targetCrewIdentifier,
    );
    props.setCurrentCrewIdentifier(targetCrewIdentifier);
    props.performIndexRefresh();
    props.setIsModalOpen(false);
  }

  function moveSelectedPlaylistsToCrew() {
    // the playlists keep their ids, so open carousels and links stay valid
    playlistMutations.movePlaylists(
      props.selectedPlaylists,
      targetCrewIdentifier,
    );
    props.setCurrentCrewIdentifier(targetCrewIdentifier);
    props.setIsModalOpen(false);
  }

  return (
    <Modal
      isOpen={props.isModalOpen}
      fullscreen="md"
      toggle={toggle}
      data-cy={CyCopyMovePlaylistsModal.CopyMovePlaylistsModal}
    >
      <ModalHeader>{t("playlist.copyMove.title")}</ModalHeader>
      <ModalBody>
        {renderLoadingUntilDefined(crews, (crewInformation) => {
          return (
            <CrewSelector
              crewInformation={crewInformation}
              targetCrewIdentifier={targetCrewIdentifier}
              setTargetCrewIdentifier={setTargetCrewIdentifier}
              size="small"
            />
          );
        })}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          {t("common.buttons.cancel")}
        </Button>{" "}
        <Button
          color="primary"
          onClick={() => moveSelectedPlaylistsToCrew()}
          data-cy={CyCopyMovePlaylistsModal.ButtonMove}
        >
          {t("playlist.copyMove.move")}
        </Button>{" "}
        <Button
          color="primary"
          onClick={() => copySelectedPlaylistsToCrew()}
          data-cy={CyCopyMovePlaylistsModal.ButtonCopy}
        >
          {t("playlist.copyMove.copy")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
